import PageLayout from '@/components/feature/PageLayout';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutInfo } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 十八禁合规指引平台',
  description: aboutInfo.brandIntro,
  about: {
    '@type': 'Organization',
    name: aboutInfo.brandName,
    description: aboutInfo.brandIntro,
    url: 'https://hyel.com.cn/about',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: aboutInfo.brandName,
  url: 'https://hyel.com.cn/',
  description: aboutInfo.brandIntro,
  contactPoint: {
    '@type': 'ContactPoint',
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    contactType: 'customer service',
    availableLanguage: 'Chinese',
    areaServed: 'CN',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressCountry: 'CN',
  },
};

export default function AboutPage() {
  return (
    <PageLayout>
      <title>关于我们 | 十八禁合规指引平台</title>
      <meta
        name="description"
        content={aboutInfo.brandIntro.substring(0, 150)}
      />
      <meta name="keywords" content="关于我们,合规指引平台,成人内容合规,品牌介绍,联系方式" />
      <link rel="canonical" href="https://hyel.com.cn/about" />
      <meta property="og:title" content="关于我们 | 十八禁合规指引平台" />
      <meta property="og:description" content={aboutInfo.brandIntro.substring(0, 150)} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://hyel.com.cn/about" />

      <SchemaOrg data={aboutSchema} />
      <SchemaOrg data={orgSchema} />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      {/* Hero */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
            {aboutInfo.brandIntro}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
              <i className="ri-focus-3-line text-lg"></i>
            </div>
            <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-3">
              我们的使命
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {aboutInfo.mission}
            </p>
          </div>
          <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
              <i className="ri-eye-line text-lg"></i>
            </div>
            <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-3">
              我们的愿景
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {aboutInfo.vision}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
            核心价值观
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {aboutInfo.values.map((value) => (
              <div
                key={value.title}
                className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
              >
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-check-double-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold font-heading text-foreground-900 mb-2">
                  {value.title}
                </h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
            发展历程
          </h2>
          <div className="space-y-6">
            {aboutInfo.milestones.map((ms, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1"></div>
                  {index < aboutInfo.milestones.length - 1 && (
                    <div className="w-px h-full min-h-[20px] bg-background-300 mt-1"></div>
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <span className="text-xs font-medium text-primary-600 whitespace-nowrap">
                    {ms.year}
                  </span>
                  <p className="text-sm text-foreground-700 mt-1">{ms.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
            团队介绍
          </h2>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
            {aboutInfo.teamIntro}
          </p>
        </div>
      </section>

      {/* Certifications */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
            资质认证
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {aboutInfo.certifications.map((cert) => (
              <div
                key={cert.name}
                className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
              >
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-verified-badge-line text-xl"></i>
                </div>
                <h4 className="text-sm font-semibold font-heading text-foreground-900 mb-2">
                  {cert.name}
                </h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <h4 className="text-xs font-medium text-foreground-500 mb-1">电子邮箱</h4>
              <p className="text-sm text-foreground-800 font-medium whitespace-nowrap">
                {aboutInfo.contact.email}
              </p>
            </div>
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-phone-line text-lg"></i>
              </div>
              <h4 className="text-xs font-medium text-foreground-500 mb-1">联系电话</h4>
              <p className="text-sm text-foreground-800 font-medium whitespace-nowrap">
                {aboutInfo.contact.phone}
              </p>
            </div>
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <h4 className="text-xs font-medium text-foreground-500 mb-1">办公地址</h4>
              <p className="text-sm text-foreground-800 font-medium">
                {aboutInfo.contact.address}
              </p>
            </div>
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-time-line text-lg"></i>
              </div>
              <h4 className="text-xs font-medium text-foreground-500 mb-1">工作时间</h4>
              <p className="text-sm text-foreground-800 font-medium whitespace-nowrap">
                {aboutInfo.contact.workingHours}
              </p>
            </div>
          </div>
        </div>
      </section>

      <BackToTop />
    </PageLayout>
  );
}